import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { BehaviorSubject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { MessageService } from 'primeng/api';
import { StatusComponent } from '@/components/shared/status/status.component';
import { ClarificationService } from '../clarification-request.service';
import {
  QuestionResponseDTO,
  QuestionAnswerBatchRequestDTO,
  GroupedQuestionsResponseDTO,
  DacQuestionsGroupDTO,
} from '@/types/clarification-request.types';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { ClarificationWorkflowBlocksService } from '../clarification-request-workflow-blocks.service';
import { getRedacteurDacGroupFormObject } from './utils/redacteur-dac-group-form-object';

export interface QuestionWithAnswer {
  question: QuestionResponseDTO;
  selected: boolean;
  answer: string;
  typeDemande: 'SIMPLE' | 'ADDITIF';
}

@Component({
  selector: 'sm-clarification-redacteur-dac-group',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    FormBuilderComponent,
    BlockBuilderComponent,
    StatusComponent,
    TranslateModule,
    SpinnerComponent,
  ],
  templateUrl: './redacteur-dac-group.component.html',
  styleUrl: './redacteur-dac-group.component.scss',
})
export class ClarificationRedacteurDacGroupComponent implements OnInit, OnDestroy {
  clarificationService = inject(ClarificationService);
  workflowBlocksService = inject(ClarificationWorkflowBlocksService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'clarification.title',
      routerLink: ['/clarification'],
    },
    {
      label: '',
      routerLink: [],
    },
  ];

  dacReference = this.activatedRoute.snapshot.queryParams['dacReference'];
  questions: QuestionResponseDTO[] = [];
  loading$ = new BehaviorSubject<boolean>(true);
  submitting$ = new BehaviorSubject<boolean>(false);

  status = 'EN_TRAITEMENT';
  taskName = 'clarification.redacteur_tasks';

  questionsWithAnswer: QuestionWithAnswer[] = [];
  selectedQuestionsCount = new BehaviorSubject<number>(0);

  formObject?: DynamicForm;
  controlBlocks: DynamicBlocks[] = [];

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.loading$.complete();
    this.submitting$.complete();
    this.selectedQuestionsCount.complete();
  }

  loadData() {
    this.loading$.next(true);
    this.clarificationService.getQuestionsForRedacteur().subscribe({
      next: (response: GroupedQuestionsResponseDTO) => {
        const group = (response.groups || []).find(g => g.dacReference === this.dacReference);

        if (!group) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('clarification.messages.error_summary'),
            detail: 'No questions found for this DAC',
          });
          this.router.navigate(['/clarification']);
          this.loading$.next(false);
          return;
        }

        this.questions = group.questions || [];

        // Initialize questions with answer state
        this.questionsWithAnswer = this.questions.map(q => ({
          question: q,
          selected: false,
          answer: q.answer || '',
          typeDemande: q.typeDemande || 'SIMPLE',
        }));

        this.breadcrumb[1].label = `${this.dacReference}`;

        this.initForm();
        this.loading$.next(false);
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('clarification.messages.error_summary'),
          detail: error.error?.message || 'Error loading questions',
        });
        this.router.navigate(['/clarification']);
        this.loading$.next(false);
      },
    });
  }

  initForm() {
    this.formObject = getRedacteurDacGroupFormObject({
      questionsWithAnswer: this.questionsWithAnswer,
      onSelectionChange: (index: number, selected: boolean) => {
        this.questionsWithAnswer[index].selected = selected;
        this.updateSelectedCount();
      },
      onAnswerChange: (index: number, answer: string) => {
        this.questionsWithAnswer[index].answer = answer;
      },
      onTypeChange: (index: number, type: 'SIMPLE' | 'ADDITIF') => {
        this.questionsWithAnswer[index].typeDemande = type;
      },
    });

    this.controlBlocks = this.workflowBlocksService.getRedacteurGroupBlocks({
      saveAnswers: () => this.saveAnswers(),
      createDemande: () => this.createDemande(),
      cancel: () => this.cancel(),
      loading: this.submitting$,
    });
  }

  updateSelectedCount() {
    const selected = this.questionsWithAnswer.filter(q => q.selected).length;
    this.selectedQuestionsCount.next(selected);
  }

  saveAnswers() {
    // Collect answers only for selected questions
    const requestDTOs: QuestionAnswerBatchRequestDTO[] = this.questionsWithAnswer
      .filter(q => q.selected && q.answer && q.answer.trim() !== '')
      .map(q => ({
        questionId: q.question.id,
        answer: q.answer,
        typeDemande: q.typeDemande,
      }));

    if (requestDTOs.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('clarification.messages.validation_summary'),
        detail: 'Veuillez sélectionner au moins une question avec une réponse à enregistrer.',
      });
      return;
    }

    this.submitting$.next(true);
    this.clarificationService.answerQuestionsBatch(requestDTOs).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('clarification.messages.success_summary'),
          detail: this.translate.instant('clarification.messages.questions_answered_success'),
        });
        this.loadData(); // Reload to get fresh data (the answered questions should disappear from this list if the API filters them)
        this.submitting$.next(false);
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('clarification.messages.error_summary'),
          detail: error.error?.message || 'Error saving answers',
        });
        this.submitting$.next(false);
      },
    });
  }

  createDemande() {
    const selectedIds = this.questionsWithAnswer
      .filter(q => q.selected)
      .map(q => q.question.id);

    if (selectedIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('clarification.messages.validation_summary'),
        detail: 'Veuillez sélectionner au moins une question.',
      });
      return;
    }

    // Check if selected questions have answers
    const unansweredSelected = this.questionsWithAnswer.filter(q => q.selected && (!q.answer || q.answer.trim() === ''));
    if (unansweredSelected.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('clarification.messages.validation_summary'),
        detail: 'Toutes les questions sélectionnées doivent avoir une réponse. Veuillez enregistrer vos réponses avant de créer un output.',
      });
      return;
    }

    this.submitting$.next(true);
    this.clarificationService.createDemandeEclaircissemnt(selectedIds).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('clarification.messages.success_summary'),
          detail: `${this.translate.instant('clarification.messages.demande_created_success')}: ${response?.reference}`,
        });
        this.router.navigate(['/clarification']);
        this.submitting$.next(false);
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('clarification.messages.error_summary'),
          detail: error.error?.message || 'Error creating demande',
        });
        this.submitting$.next(false);
      },
    });
  }

  cancel() {
    this.router.navigate(['/clarification']);
  }
}
